import React from 'react';
import {
  PolicyContainer,
  PolicyHeader,
  PolicyTitle,
  EffectiveDate,
  PolicySection,
  PolicySectionTitle,
  PolicySubTitle,
  PolicyText,
  PolicyList,
  HighlightBox,
  ContactInfo,
  TableOfContents,
  TOCLink
} from './SharedStyledComponents';

const BeestmodePrivacyPolicyPageEN = () => {
  return (
    <PolicyContainer style={{ direction: 'ltr', textAlign: 'left' }}>
      <PolicyHeader>
        <PolicyTitle>Privacy Policy</PolicyTitle>
        <EffectiveDate>Last updated: September 7, 2026</EffectiveDate>
      </PolicyHeader>

      <PolicySection>
        <PolicySectionTitle>Table of Contents</PolicySectionTitle>
        <TableOfContents>
          <TOCLink href="#overview">Overview</TOCLink>
          <TOCLink href="#data-collection">Data Collection</TOCLink>
          <TOCLink href="#data-usage">Data Usage</TOCLink>
          <TOCLink href="#data-sharing">Data Sharing</TOCLink>
          <TOCLink href="#data-security">Data Security</TOCLink>
          <TOCLink href="#user-rights">User Rights</TOCLink>
          <TOCLink href="#children-privacy">Children's Privacy</TOCLink>
          <TOCLink href="#international">International Users</TOCLink>
          <TOCLink href="#cookies">Cookies and Tracking Technologies</TOCLink>
          <TOCLink href="#data-retention">Data Retention</TOCLink>
          <TOCLink href="#third-party-services">Third-Party Services</TOCLink>
          <TOCLink href="#app-specific">App-Specific Permissions</TOCLink>
          <TOCLink href="#changes">Policy Changes</TOCLink>
          <TOCLink href="#contact">Contact Us</TOCLink>
        </TableOfContents>
      </PolicySection>

      <PolicySection id="overview">
        <PolicySectionTitle>1. Overview</PolicySectionTitle>
        <PolicyText>
          Our application is committed to protecting your privacy. This privacy policy explains how we collect, use, and protect your personal information when you use our application.
        </PolicyText>
        <PolicyText>
          This policy applies to information collected through the application and Beestmode websites, including the app-download landing page.
        </PolicyText>
        <HighlightBox>
          <strong>Important:</strong> Use of the application or website is subject to this policy. Meta marketing technologies on the landing page are enabled only after your explicit choice.
        </HighlightBox>
      </PolicySection>

      <PolicySection id="data-collection">
        <PolicySectionTitle>2. Data Collection</PolicySectionTitle>
        
        <PolicySubTitle>2.1 Information You Provide</PolicySubTitle>
        <PolicyText>We may collect the following information that you explicitly provide:</PolicyText>
        <PolicyList>
          <li>Registration details (name, email address)</li>
          <li>Profile information (photo, preferences)</li>
          <li>Content you create or upload</li>
          <li>Messages and support requests</li>
          <li>Payment information (through secure payment systems)</li>
        </PolicyList>

        <PolicySubTitle>2.2 Automatically Collected Information</PolicySubTitle>
        <PolicyText>The application may automatically collect technical information:</PolicyText>
        <PolicyList>
          <li>Device information (model, operating system, app version)</li>
          <li>Network information (IP address, service provider)</li>
          <li>Usage data (usage time, in-app actions)</li>
          <li>Location information (with explicit consent only)</li>
          <li>Unique identifiers (such as device or vendor identifiers; the IDFA advertising identifier is available only after Apple tracking permission is granted)</li>
        </PolicyList>

        <PolicySubTitle>2.3 Third-Party Information</PolicySubTitle>
        <PolicyText>We may receive information from third parties:</PolicyText>
        <PolicyList>
          <li>Social networks (when logging in through them)</li>
          <li>Analytics services (Google Analytics, Firebase)</li>
          <li>Advertising services (advertising identifiers)</li>
          <li>Meta App Events (app events and technical information for install measurement and advertising attribution)</li>
          <li>Payment services (transaction information)</li>
        </PolicyList>
      </PolicySection>

      <PolicySection id="data-usage">
        <PolicySectionTitle>3. Data Usage</PolicySectionTitle>
        <PolicyText>We use the collected information for the following purposes:</PolicyText>

        <PolicySubTitle>3.1 Primary Purposes</PolicySubTitle>
        <PolicyList>
          <li>Providing the service and operating the application</li>
          <li>Creating and managing user accounts</li>
          <li>Processing payments and subscriptions</li>
          <li>Providing technical support</li>
          <li>Improving and developing the application</li>
        </PolicyList>

        <PolicySubTitle>3.2 Secondary Purposes</PolicySubTitle>
        <PolicyList>
          <li>Sending notifications and updates</li>
          <li>Research and analytics</li>
          <li>Security and fraud prevention</li>
          <li>Personalized advertising (with consent)</li>
          <li>Legal compliance</li>
        </PolicyList>

        <HighlightBox>
          <strong>Note:</strong> We will not use your information for purposes not specified in this policy without your explicit consent.
        </HighlightBox>
      </PolicySection>

      <PolicySection id="data-sharing">
        <PolicySectionTitle>4. Data Sharing</PolicySectionTitle>
        <PolicyText>We do not sell or rent personal information to third parties. However, we may share information in the following situations:</PolicyText>

        <PolicySubTitle>4.1 Service Providers</PolicySubTitle>
        <PolicyList>
          <li>Cloud hosting services (AWS, Google Cloud)</li>
          <li>Analytics services (Firebase Analytics)</li>
          <li>Meta Platforms (app-event reporting, campaign measurement, and advertising attribution)</li>
          <li>Payment services (Apple Pay, Stripe)</li>
          <li>Notification services (Apple Push Notifications)</li>
          <li>Technical support services</li>
        </PolicyList>

        <PolicySubTitle>4.2 Legal Requirements</PolicySubTitle>
        <PolicyText>We may disclose information when:</PolicyText>
        <PolicyList>
          <li>Required by law or court order</li>
          <li>Protecting our legal rights</li>
          <li>Investigating security breaches</li>
          <li>Protecting user safety</li>
        </PolicyList>

        <PolicySubTitle>4.3 Business Transactions</PolicySubTitle>
        <PolicyText>In case of merger, acquisition, or asset sale, information may be transferred to the acquiring party.</PolicyText>
      </PolicySection>

      <PolicySection id="data-security">
        <PolicySectionTitle>5. Data Security</PolicySectionTitle>
        <PolicyText>We implement advanced security measures to protect your information:</PolicyText>

        <PolicySubTitle>5.1 Technical Security Measures</PolicySubTitle>
        <PolicyList>
          <li>Advanced encryption (AES-256) for sensitive data</li>
          <li>Secure communication (HTTPS/TLS)</li>
          <li>Two-factor authentication</li>
          <li>Continuous threat monitoring</li>
          <li>Encrypted backups</li>
        </PolicyList>

        <PolicySubTitle>5.2 Organizational Security Measures</PolicySubTitle>
        <PolicyList>
          <li>Data access limited to authorized employees only</li>
          <li>Information security training for employees</li>
          <li>Regular security audits</li>
          <li>Strong password policies</li>
        </PolicyList>

        <PolicySubTitle>5.3 Data Storage</PolicySubTitle>
        <PolicyText>
          Your data is stored on secure servers in certified data centers. We only use hosting providers that meet international security standards.
        </PolicyText>
      </PolicySection>

      <PolicySection id="user-rights">
        <PolicySectionTitle>6. User Rights</PolicySectionTitle>
        <PolicyText>You have several rights regarding your personal information:</PolicyText>

        <PolicySubTitle>6.1 Right of Access</PolicySubTitle>
        <PolicyText>You may request a copy of all personal information about you in our possession.</PolicyText>

        <PolicySubTitle>6.2 Right of Rectification</PolicySubTitle>
        <PolicyText>You may request correction of incorrect or inaccurate information.</PolicyText>

        <PolicySubTitle>6.3 Right of Erasure</PolicySubTitle>
        <PolicyText>You may request deletion of your personal information, subject to legal requirements.</PolicyText>

        <PolicySubTitle>6.4 Right to Restrict Processing</PolicySubTitle>
        <PolicyText>You may request restriction of information processing in certain situations.</PolicyText>

        <PolicySubTitle>6.5 Right to Data Portability</PolicySubTitle>
        <PolicyText>You may request transfer of your information to another provider.</PolicyText>

        <PolicySubTitle>6.6 Right to Object</PolicySubTitle>
        <PolicyText>You may object to processing information for advertising or marketing purposes.</PolicyText>

        <HighlightBox>
          <strong>How to Exercise Rights:</strong> To request account and associated-data deletion, use our <a href="/beestmode/delete-account">Beestmode account deletion request page</a>. For other privacy requests, contact us using the contact details at the end of this policy.
        </HighlightBox>
      </PolicySection>

      <PolicySection id="children-privacy">
        <PolicySectionTitle>7. Children's Privacy</PolicySectionTitle>
        <PolicyText>
          Our application is intended for users aged 13 and above. We do not knowingly collect personal information from children under 13.
        </PolicyText>
        <PolicyText>
          If we learn that we have collected information from a child under 13, we will immediately delete the information from our systems.
        </PolicyText>
        <PolicyText>
          Parents or guardians who suspect that a child under 13 has provided personal information are invited to contact us immediately.
        </PolicyText>
      </PolicySection>

      <PolicySection id="international">
        <PolicySectionTitle>8. International Users</PolicySectionTitle>
        <PolicyText>
          The application is available to international users, and we are committed to complying with international privacy regulations.
        </PolicyText>

        <PolicySubTitle>8.1 GDPR (General Data Protection Regulation)</PolicySubTitle>
        <PolicyText>Users in the European Union have additional rights under GDPR:</PolicyText>
        <PolicyList>
          <li>Right to explicit consent for information processing</li>
          <li>Right to erasure ("right to be forgotten")</li>
          <li>Right to data portability</li>
          <li>Right to file a complaint with supervisory authority</li>
        </PolicyList>

        <PolicySubTitle>8.2 CCPA (California Consumer Privacy Act)</PolicySubTitle>
        <PolicyText>California residents are entitled to:</PolicyText>
        <PolicyList>
          <li>Know what personal information is collected</li>
          <li>Know if information is sold or disclosed</li>
          <li>Opt-out of sale of personal information</li>
          <li>Access personal information</li>
          <li>Right to equal service and price</li>
        </PolicyList>

        <PolicySubTitle>8.3 International Data Transfer</PolicySubTitle>
        <PolicyText>
          Data may be transferred and processed in different countries. We ensure that transfers are made in accordance with relevant privacy regulations.
        </PolicyText>
      </PolicySection>

      <PolicySection id="cookies">
        <PolicySectionTitle>9. Cookies and Tracking Technologies</PolicySectionTitle>
        <PolicyText>
          The application uses various technologies to improve experience and for analytical purposes.
        </PolicyText>

        <PolicySubTitle>9.1 Types of Technologies</PolicySubTitle>
        <PolicyList>
          <li>Device unique identifiers</li>
          <li>Advertising IDs (IDFA on iOS)</li>
          <li>Analytics data (Firebase Analytics)</li>
          <li>Tracking pixels for advertising</li>
        </PolicyList>

        <PolicySubTitle>9.2 Usage Purposes</PolicySubTitle>
        <PolicyList>
          <li>Improving application performance</li>
          <li>Understanding usage patterns</li>
          <li>Personalized advertising</li>
          <li>Fraud prevention</li>
          <li>Technical support</li>
        </PolicyList>

        <PolicySubTitle>9.3 Controlling Tracking Technologies</PolicySubTitle>
        <PolicyText>You can control tracking technologies through:</PolicyText>
        <PolicyList>
          <li>iOS Settings under Privacy &amp; Security &gt; Tracking</li>
          <li>Accepting or declining Apple's App Tracking Transparency request</li>
          <li>Opting out of analytics</li>
        </PolicyList>

        <PolicySubTitle>9.4 Landing Page, Private Analytics, and Meta Pixel</PolicySubTitle>
        <PolicyText>
          On the Beestmode download landing page, we use a local browser identifier for our private analytics so we can distinguish a first visit from a returning visit. A visit may send our analytics server the local time, device type, banner code, page interactions, and approximate location only when it is already available and sharing is permitted. The page does not request location permission.
        </PolicyText>
        <PolicyText>
          Meta Pixel is blocked by default and is enabled only after you select “Approve” in the marketing-cookie notice. Once approved, we send Meta PageView and BeestmodeLandingView events, the banner code, and the detected device type for campaign measurement, Custom Audiences, and retargeting. Meta may also receive the IP address, page URL, browser and device details, and Meta identifiers or cookies under its own policy.
        </PolicyText>
        <PolicyText>
          From the landing page, we do not send Meta our private visitor identifier, precise location, or health, workout, or in-app activity data. You can reject or withdraw consent at any time through “Cookie settings” at the bottom of the landing page. Your choice does not affect app downloads. The application's use of the Meta SDK is described separately in the next section.
        </PolicyText>

        <PolicySubTitle>9.5 Meta SDK in the iOS Application</PolicySubTitle>
        <PolicyText>
          The iOS application includes Meta's Facebook SDK Core. The SDK sends app lifecycle events, such as app activation or launch, and may send app-install information, device and app details, device identifiers, and other diagnostic or analytics information. We use this information for campaign and install measurement, advertising attribution, analytics, app operation, and troubleshooting. We do not configure events containing health information, workout content, or user-generated content for transmission to Meta.
        </PolicyText>
        <PolicyText>
          On iOS 14.5 and later, the application requests Apple's App Tracking Transparency permission before enabling advertiser tracking or accessing the IDFA advertising identifier. If you decline, the application will not make the IDFA available to Meta for tracking across apps and websites owned by other companies, and the application's core features will remain available. Even without permission, limited non-IDFA technical information and events may be sent to Meta, and aggregated attribution information may be received through Apple's SKAdNetwork, subject to platform rules.
        </PolicyText>
        <PolicyText>
          You can change tracking permission at any time in iOS Settings under Privacy &amp; Security &gt; Tracking. Meta processes information it receives according to <a href="https://www.facebook.com/privacy/policy/" target="_blank" rel="noreferrer">Meta's Privacy Policy</a>.
        </PolicyText>
      </PolicySection>

      <PolicySection id="data-retention">
        <PolicySectionTitle>10. Data Retention</PolicySectionTitle>
        <PolicyText>
          We retain your information as long as necessary for the purposes stated in this policy.
        </PolicyText>

        <PolicySubTitle>10.1 Retention Periods</PolicySubTitle>
        <PolicyList>
          <li>Account information: until account deletion</li>
          <li>Usage data: 24 months</li>
          <li>Security logs: 12 months</li>
          <li>Support information: 36 months</li>
          <li>Financial data: 7 years (legal requirement)</li>
        </PolicyList>

        <PolicySubTitle>10.2 Data Deletion</PolicySubTitle>
        <PolicyText>
          At the end of the retention period or upon your request, information will be securely deleted from all our systems.
        </PolicyText>
      </PolicySection>

      <PolicySection id="third-party-services">
        <PolicySectionTitle>11. Third-Party Services</PolicySectionTitle>
        <PolicyText>The application uses the following third-party services:</PolicyText>

        <PolicySubTitle>11.1 Apple Services</PolicySubTitle>
        <PolicyList>
          <li>Apple App Store (downloads and updates)</li>
          <li>Apple Pay (payments)</li>
          <li>Apple Push Notifications (notifications)</li>
          <li>iCloud (data backup - optional)</li>
        </PolicyList>

        <PolicySubTitle>11.2 Google Services</PolicySubTitle>
        <PolicyList>
          <li>Firebase Analytics (analytics)</li>
          <li>Firebase Crashlytics (crash reporting)</li>
          <li>Google Cloud Platform (hosting)</li>
        </PolicyList>

        <PolicySubTitle>11.3 Meta</PolicySubTitle>
        <PolicyList>
          <li>Meta Pixel (campaign measurement, Custom Audiences, and retargeting — with consent)</li>
          <li>Facebook SDK Core and Meta App Events (install and campaign measurement, advertising attribution, analytics, and app operation)</li>
        </PolicyList>

        <PolicySubTitle>11.4 Additional Services</PolicySubTitle>
        <PolicyList>
          <li>Payment systems (Stripe, PayPal)</li>
          <li>Email services (SendGrid)</li>
          <li>CDN services (CloudFlare)</li>
        </PolicyList>

        <PolicyText>
          Each third-party service is subject to its own privacy policy. We recommend reviewing these policies.
        </PolicyText>
      </PolicySection>

      <PolicySection id="app-specific">
        <PolicySectionTitle>12. App-Specific Permissions</PolicySectionTitle>
        <PolicyText>The application may request the following permissions:</PolicyText>

        <PolicySubTitle>12.1 Required Permissions</PolicySubTitle>
        <PolicyList>
          <li><strong>Internet:</strong> For communication with servers</li>
          <li><strong>Storage:</strong> For saving local data</li>
          <li><strong>Notifications:</strong> For sending updates</li>
        </PolicyList>

        <PolicySubTitle>12.2 Optional Permissions</PolicySubTitle>
        <PolicyList>
          <li><strong>Location:</strong> For location-based services (with consent)</li>
          <li><strong>Camera:</strong> For photo capture or scanning (with consent)</li>
          <li><strong>Contacts:</strong> For sharing with friends (with consent)</li>
          <li><strong>Calendar:</strong> For reminders and events (with consent)</li>
        </PolicyList>

        <PolicyText>
          You can change permissions at any time through your device's iOS settings.
        </PolicyText>
      </PolicySection>

      <PolicySection id="changes">
        <PolicySectionTitle>13. Policy Changes</PolicySectionTitle>
        <PolicyText>
          We may update this privacy policy from time to time. Changes will be effective from the moment of publication in the application.
        </PolicyText>

        <PolicySubTitle>13.1 Notification of Changes</PolicySubTitle>
        <PolicyList>
          <li>In-app notification for material changes</li>
          <li>Email to registered users</li>
          <li>Update of "last updated" date at the top of the policy</li>
        </PolicyList>

        <PolicySubTitle>13.2 Types of Changes</PolicySubTitle>
        <PolicyText><strong>Material changes</strong> - will require renewed consent:</PolicyText>
        <PolicyList>
          <li>Change in information collection purposes</li>
          <li>Addition of new third parties</li>
          <li>Change in data retention periods</li>
        </PolicyList>

        <PolicyText><strong>Technical changes</strong> - will take effect without additional consent:</PolicyText>
        <PolicyList>
          <li>Contact detail updates</li>
          <li>Text clarifications</li>
          <li>Error corrections</li>
        </PolicyList>
      </PolicySection>

      <PolicySection id="contact">
        <PolicySectionTitle>14. Contact Us</PolicySectionTitle>
        <PolicyText>
          For any questions regarding this privacy policy or the handling of your personal information, you can contact us:
        </PolicyText>

        <ContactInfo>
          <PolicySubTitle>Contact Details</PolicySubTitle>
          <PolicyText><strong>Email:</strong> haggaimagen87@gmail.com</PolicyText>
          <PolicyText><strong>Phone:</strong> 050-5456644</PolicyText>
          {/* <PolicyText><strong>Address:</strong> 123 Main Street, Tel Aviv, Israel</PolicyText> */}
          <PolicyText><strong>Response Hours:</strong> Sunday - Thursday, 9:00-17:00</PolicyText>
        </ContactInfo>

        <PolicySubTitle>14.1 Handling Inquiries</PolicySubTitle>
        <PolicyText>We commit to handling every inquiry within:</PolicyText>
        <PolicyList>
          <li>Receipt confirmation: within 24 hours</li>
          <li>Initial response: within 72 hours</li>
          <li>Full handling: within 30 days</li>
        </PolicyList>

        <PolicySubTitle>14.2 Privacy Commissioner</PolicySubTitle>
        <PolicyText>
          If you are not satisfied with the handling of your inquiry, you may contact the Privacy Commissioner in Israel:
        </PolicyText>
        <PolicyText>
          {/* <strong>Website:</strong> www.gov.il/privacy<br/> */}
          <strong>Phone:</strong> 050-5456644
        </PolicyText>
      </PolicySection>

      <PolicySection>
        <HighlightBox>
          <PolicyText style={{marginBottom: 0}}>
            <strong>Last update date:</strong> September 7, 2026<br/>
            <strong>Policy version:</strong> 2.3<br/>
            <strong>Original language:</strong> Hebrew
          </PolicyText>
        </HighlightBox>
      </PolicySection>
    </PolicyContainer>
  );
};

export default BeestmodePrivacyPolicyPageEN;
